<template>
  <component :is="embedded ? 'div' : 'v-main'">
    <component :is="embedded ? 'div' : 'v-container'" v-bind="embedded ? {} : { fluid: true }">
      <v-card :elevation="embedded ? 0 : 6" :rounded="embedded ? undefined : 'xxl'">
        <!-- Embedded (dialog) use gets its banner header from AppDialog instead. -->
        <v-card-title v-if="!embedded" class="mt-4">
          <v-btn
            class="mx-2"
            fab
            x-small
            color="primary"
            @click="$router.back()"
          >
            <v-icon dark> mdi-arrow-left </v-icon>
          </v-btn>
          Edit Company Stock
        </v-card-title>
        <v-card-text>
          <v-row>
            <!-- Product Selection -->
            <v-col sm="6">
              <v-autocomplete
                v-model="selectedProduct"
                :items="filteredProducts"
                outlined
                item-text="name"
                item-value="id"
                prepend-inner-icon="mdi-package-variant"
                :error-messages="errors.product_id"
                @change="onProductChange"
                clearable
                :no-data-text="isLoading ? 'Loading products...' : 'No products with available variants'"
              >
                <template v-slot:label>Select Product <span style="color:#f44336;font-weight:bold;">*</span></template>
              </v-autocomplete>
            </v-col>

            <!-- Variant Selection -->
            <v-col sm="6">
              <v-autocomplete
                v-model="form.variant_id"
                :items="filteredVariants"
                outlined
                :item-text="formatVariantName"
                item-value="id"
                prepend-inner-icon="mdi-format-list-bulleted-square"
                :error-messages="errors.variant_id"
                :disabled="!selectedProduct"
                clearable
                :no-data-text="!selectedProduct ? 'Select a product first' : 'No available variants for this product'"
              >
                <template v-slot:label>Select Product Variant <span style="color:#f44336;font-weight:bold;">*</span></template>
              </v-autocomplete>
            </v-col>

            <!-- Stock Operation Type -->
            <v-col sm="12">
              <v-radio-group
                v-model="stockOperation"
                row
                label="Stock Operation"
                @change="onOperationChange"
              >
                <v-radio
                  label="Direct Update (Set Exact Quantity)"
                  value="direct"
                  color="primary"
                ></v-radio>
                <v-radio
                  label="Increase Stock"
                  value="increase"
                  color="success"
                ></v-radio>
                <v-radio
                  label="Decrease Stock"
                  value="decrease"
                  color="error"
                ></v-radio>
              </v-radio-group>
            </v-col>

            <!-- Current Stock Display -->
            <v-col sm="12" v-if="stockOperation !== 'direct'">
              <v-alert type="info" dense outlined>
                <strong>Current Stock:</strong> {{ originalQuantity }} units
              </v-alert>
            </v-col>

            <!-- Warning for insufficient stock -->
            <v-col sm="12" v-if="stockOperation === 'decrease' && quantityChange && parseInt(quantityChange) > originalQuantity">
              <v-alert type="error" dense>
                <v-icon left>mdi-alert-circle</v-icon>
                <strong>Error:</strong> Cannot decrease by {{ quantityChange }} units.
                You only have {{ originalQuantity }} units in stock.
              </v-alert>
            </v-col>

            <!-- Quantity Change (for increase/decrease) -->
            <v-col sm="6" v-if="stockOperation !== 'direct'">
              <v-text-field
                v-model="quantityChange"
                outlined
                :label="stockOperation === 'increase' ? 'Quantity to Add' : 'Quantity to Remove'"
                type="number"
                min="1"
                prepend-inner-icon="mdi-plus-minus"
                :error-messages="errors.quantity_change"
                @input="calculateNewQuantity"
              ></v-text-field>
            </v-col>

            <!-- New Quantity Preview (for increase/decrease) -->
            <v-col sm="6" v-if="stockOperation !== 'direct' && quantityChange">
              <v-text-field
                :value="form.quantity"
                outlined
                label="New Total Quantity"
                readonly
                prepend-inner-icon="mdi-numeric"
                :class="stockOperation === 'increase' ? 'green--text' : 'red--text'"
              ></v-text-field>
            </v-col>

            <!-- Direct Quantity Update -->
            <v-col sm="6" v-if="stockOperation === 'direct'">
              <v-text-field
                v-model="form.quantity"
                outlined
                label="Quantity"
                type="number"
                min="0"
                prepend-inner-icon="mdi-numeric"
                :error-messages="errors.quantity"
              ></v-text-field>
            </v-col>

            <!-- Is Active -->
            <v-col sm="6">
              <v-switch
                v-model="form.is_active"
                label="Is Active"
                color="primary"
                :true-value="1"
                :false-value="0"
                class="mt-2"
              ></v-switch>
            </v-col>

            <!-- Selected Variant Details -->
            <v-col cols="12" v-if="selectedVariant">
              <v-card outlined class="pa-3">
                <h4 class="mb-2">Selected Variant Details:</h4>
                <v-row>
                  <v-col sm="3">
                    <strong>Color:</strong> {{ selectedVariant.color || "N/A" }}
                  </v-col>
                  <v-col sm="3">
                    <strong>Size:</strong> {{ selectedVariant.size || "N/A" }}
                  </v-col>
                  <v-col sm="3">
                    <strong>Price:</strong> ${{ selectedVariant.price || "0.00" }}
                  </v-col>
                  <v-col sm="3">
                    <strong>Discount:</strong> {{ selectedVariant.discount_percentage || 0 }}%
                  </v-col>
                </v-row>
              </v-card>
            </v-col>
          </v-row>

          <div class="mt-6">
            <v-btn text class="mr-2" @click="embedded ? $emit('close') : $router.back()">Cancel</v-btn>
            <v-btn
              rounded
              color="primary"
              :loading="isLoading"
              :disabled="isLoading || hasStockError"
              @click="save"
            >
              Update Company Stock
            </v-btn>
          </div>
        </v-card-text>
      </v-card>

      <!-- Success Dialog Box - not shown for embedded (dialog) use, which
           closes and lets the parent list refresh instead. -->
      <v-dialog v-if="!embedded" v-model="isSuccessDialog" width="600">
        <v-card rounded="xxl">
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon
                  large
                  style="color: green; font-size: 90px; width: 200px !important"
                >
                  mdi-check-circle
                </v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h1 style="color: green">Success!</h1>
                      <br />
                      <h3>Company Stock updated successfully.</h3>
                      <br />
                      <v-btn
                        color="success"
                        @click="isSuccessDialog = false, $router.push('/company-stocks')"
                      >
                        OK
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>
    </component>
  </component>
</template>

<script>
import axios from "axios";

export default {
  name: "CompanyStockEditPage",
  props: {
    // When true, this form is rendered inside another page's dialog (e.g.
    // the Company Stocks list's inline "Edit Company Stock") instead of its
    // own routed page - the id comes from the `stockId` prop rather than
    // the route, and it emits `saved`/`close` rather than navigating away.
    embedded: { type: Boolean, default: false },
    stockId: { type: String, default: null },
  },
  computed: {
    errors() {
      return this.$store.getters["errors/getErrors"] || {};
    },
    // Check if there's a stock error (trying to decrease more than available)
    hasStockError() {
      if (this.stockOperation === 'decrease' && this.quantityChange) {
        return parseInt(this.quantityChange) > this.originalQuantity;
      }
      return false;
    },
    // Filter products to only show those with available variants (or contains the current variant)
    filteredProducts() {
      if (!this.products || this.products.length === 0) return [];

      return this.products.filter(product => {
        // Check if product has any variants
        if (!product.product_variants || product.product_variants.length === 0) {
          return false;
        }

        // Always show the product that contains the current variant being edited
        const hasCurrentVariant = product.product_variants.some(
          variant => variant.id === this.originalVariantId
        );

        if (hasCurrentVariant) return true;

        // Check if at least one variant is not already added (excluding current)
        const hasAvailableVariants = product.product_variants.some(
          variant => !this.existingVariantIds.includes(variant.id) || variant.id === this.originalVariantId
        );

        return hasAvailableVariants;
      }).map(product => {
        // Count available variants for display
        // const availableCount = product.product_variants.filter(
        //   v => !this.existingVariantIds.includes(v.id) || v.id === this.originalVariantId
        // ).length;
        // const totalCount = product.product_variants.length;

        return {
          ...product,
          // name: `${product.name} (${availableCount}/${totalCount} variants available)`
          name: `${product.name}`
        };
      });
    },

    // Filter variants to exclude already added ones (except the current one)
    filteredVariants() {
      if (!this.productVariants || this.productVariants.length === 0) return [];

      return this.productVariants.filter(
        variant => !this.existingVariantIds.includes(variant.id) || variant.id === this.originalVariantId
      );
    }
  },
  data() {
    return {
      form: {
        id: null,
        variant_id: null,
        quantity: "",
        is_active: 1,
      },
      stockOperation: 'direct', // direct, increase, decrease
      quantityChange: null, // Amount to increase or decrease
      originalQuantity: 0, // Store original quantity
      selectedProduct: null,
      products: [],
      productVariants: [],
      selectedVariant: null,
      isLoading: false,
      isSuccessDialog: false,
      old_json: null,
      existingCompanyStocks: [],
      existingVariantIds: [],
      originalVariantId: null, // Store the original variant ID being edited
    };
  },
  created() {
    this.$store.dispatch("errors/setErrors", {});
    this.getData();
    this.getExistingCompanyStocks();
    this.getProducts();
  },
  methods: {
    async getExistingCompanyStocks() {
      try {
        const response = await axios.get("/company_stocks");
        this.existingCompanyStocks = response.data.data;
        // Extract all existing variant IDs (will exclude current one later)
        this.existingVariantIds = this.existingCompanyStocks.map(stock => stock.variant_id);
      } catch (error) {
        console.log("Error fetching existing company stocks:", error);
      }
    },
    async getData() {
      this.isLoading = true;
      const encryptedId = this.stockId || this.$route.params.stockId;

      try {
        const response = await axios.get(`/company_stocks/${encryptedId}`);
        const stockData = response.data.data;

        this.form = {
          id: stockData.id,
          variant_id: stockData.variant_id,
          quantity: stockData.quantity,
          is_active: stockData.is_active,
        };

        // Store the original variant ID and quantity
        this.originalVariantId = stockData.variant_id;
        this.originalQuantity = stockData.quantity;

        // Store original data for user timestamp
        this.old_json = JSON.parse(JSON.stringify(stockData));

        // If variant exists, load its product and variants
        if (stockData.variant) {
          // Find the product that contains this variant
          await this.getProducts();
          const product = this.products.find(p =>
            p.product_variants &&
            p.product_variants.some(v => v.id === stockData.variant_id)
          );

          if (product) {
            this.selectedProduct = product.id;
            this.productVariants = product.product_variants;
            this.selectedVariant = product.product_variants.find(
              v => v.id === stockData.variant_id
            );
          }
        }
      } catch (error) {
        console.log("Error fetching data:", error);
        if (this.$toast) this.$toast.error("Failed to load company stock data");
      } finally {
        this.isLoading = false;
      }
    },
    async getProducts() {
      try {
        const response = await axios.get("/products");
        this.products = response.data.data;
      } catch (error) {
        console.log("Error fetching products:", error);
      }
    },
    onProductChange() {
      // Clear any previous errors when product changes
      this.$store.dispatch("errors/setErrors", {});

      // Only reset if actually changing product
      if (!this.selectedProduct) {
        this.form.variant_id = null;
        this.productVariants = [];
        this.selectedVariant = null;
        return;
      }

      // Find the selected product and get its variants
      const product = this.products.find(p => p.id === this.selectedProduct);
      if (product && product.product_variants) {
        this.productVariants = product.product_variants;

        // Check if current variant is in the new product's variants
        const variantExists = product.product_variants.some(
          v => v.id === this.form.variant_id
        );

        if (!variantExists) {
          this.form.variant_id = null;
          this.selectedVariant = null;
        }
      }
    },
    formatVariantName(variant) {
      let name = [];
      if (variant.color) name.push(`Color: ${variant.color}`);
      if (variant.size) name.push(`Size: ${variant.size}`);
      if (variant.price) name.push(`$${variant.price}`);

      const formattedName = name.length > 0 ? name.join(" | ") : "Variant";

      // Add indicator if variant is already added (and it's not the current one)
      if (this.existingVariantIds.includes(variant.id) && variant.id !== this.originalVariantId) {
        return `${formattedName} (Already added)`;
      }

      // Mark the current variant being edited
      if (variant.id === this.originalVariantId) {
        return `${formattedName} (Current)`;
      }

      return formattedName;
    },
    onOperationChange() {
      // Reset quantity change when operation changes
      this.quantityChange = null;
      this.$store.dispatch("errors/setErrors", {});

      // Reset quantity to original if switching back to direct
      if (this.stockOperation === 'direct') {
        this.form.quantity = this.originalQuantity;
      }
    },
    calculateNewQuantity() {
      if (!this.quantityChange || this.quantityChange <= 0) {
        this.form.quantity = this.originalQuantity;
        return;
      }

      const changeAmount = parseInt(this.quantityChange);

      if (this.stockOperation === 'increase') {
        this.form.quantity = this.originalQuantity + changeAmount;
      } else if (this.stockOperation === 'decrease') {
        if (changeAmount > this.originalQuantity) {
          // Show error: trying to decrease more than available
          this.$store.dispatch("errors/setErrors", {
            quantity_change: [`Cannot decrease by ${changeAmount} units. Current stock is only ${this.originalQuantity} units.`]
          });
          this.form.quantity = 0;
        } else {
          const newQty = this.originalQuantity - changeAmount;
          this.form.quantity = newQty;
          this.$store.dispatch("errors/setErrors", {});
        }
      }
    },
    async save() {
      try {
        this.isLoading = true;
        this.$store.dispatch("errors/setErrors", {});

        // Validate
        let validationErrors = {};
        if (!this.form.variant_id) {
          validationErrors.variant_id = ["Product variant is required"];
        }
        if (this.form.quantity === null || this.form.quantity === "" || this.form.quantity < 0) {
          validationErrors.quantity = ["Quantity must be 0 or greater"];
        }

        // Validate operation-specific fields
        if (this.stockOperation !== 'direct') {
          if (!this.quantityChange || this.quantityChange <= 0) {
            validationErrors.quantity_change = ["Please enter the quantity to " + this.stockOperation];
          } else if (this.stockOperation === 'decrease' && parseInt(this.quantityChange) > this.originalQuantity) {
            // Validate decrease doesn't exceed available quantity
            validationErrors.quantity_change = [
              `Cannot decrease by ${this.quantityChange} units. Current stock is only ${this.originalQuantity} units.`
            ];
          }
        }

        // Validate direct update doesn't go negative
        if (this.stockOperation === 'direct' && this.form.quantity < 0) {
          validationErrors.quantity = ["Quantity cannot be negative"];
        }

        if (Object.keys(validationErrors).length > 0) {
          this.$store.dispatch("errors/setErrors", validationErrors);
          this.isLoading = false;
          return;
        }

        const encryptedId = this.stockId || this.$route.params.stockId;

        // Prepare request data based on operation type
        const requestData = {
          ...this.form
        };

        if (this.stockOperation !== 'direct') {
          requestData.stock_operation = this.stockOperation;
          requestData.quantity_change = parseInt(this.quantityChange);
        }

        const response = await axios.patch(`/company_stocks/${encryptedId}`, requestData);

        if (response.data.success) {
          await this.saveUserTimestamp();
          if (this.embedded) {
            this.$emit("saved");
            this.$emit("close");
          } else {
            this.isSuccessDialog = true;
          }
        }
      } catch (error) {
        console.log("Save error:", error);
        if (error.response && error.response.data) {
          // Handle Laravel validation errors (422 status)
          if (error.response.status === 422 && error.response.data.errors) {
            this.$store.dispatch("errors/setErrors", error.response.data.errors);
          } else if (error.response.data.data) {
            this.$store.dispatch("errors/setErrors", error.response.data.data);
          } else if (error.response.data.message) {
            // Handle general error message
            if (this.$toast) this.$toast.error(error.response.data.message);
          } else {
            if (this.$toast) this.$toast.error("Failed to update company stock");
          }
        } else {
          if (this.$toast) this.$toast.error("Failed to update company stock");
        }
      } finally {
        this.isLoading = false;
      }
    },
    async saveUserTimestamp() {
      try {
        if (!this.user || !this.user.id) {
          return;
        }
        const form = {
          from_path: `/company-stocks/${this.form.id}`,
          from_name: "CompanyStockEditPage",
          user_id: this.user.id,
          old_json: JSON.stringify(this.old_json),
          new_json: JSON.stringify(this.form),
        };
        await axios.post(`/user_timestamps`, form);
      } catch (error) {
        console.log("Timestamp save error:", error);
      }
    },
  },
  watch: {
    "form.variant_id": function(newVal) {
      if (newVal) {
        this.selectedVariant = this.productVariants.find(v => v.id === newVal);
      } else {
        this.selectedVariant = null;
      }
    },
  },
};
</script>

<style scoped>
</style>